import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Search, FileText, FlaskConical, Link2 } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

export const AdminLabResults = () => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const fileRef = useRef(null);
  const targetRef = useRef(null);

  const load = useCallback(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const pick = (p) => {
    targetRef.current = p;
    fileRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const p = targetRef.current;
    if (!file || !p) return;
    setUploadingId(p.id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/products/${p.id}/lab-result`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Lab result uploaded for ${p.name} ${p.dosage}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  const openLink = (p) => {
    setLinkTarget(p);
    setLinkUrl(p.lab_result_url?.startsWith("http") ? p.lab_result_url : "");
  };

  const saveLink = async () => {
    setSavingLink(true);
    try {
      await api.put(`/admin/products/${linkTarget.id}/lab-result-link`, { url: linkUrl.trim() });
      toast.success(`Lab result linked for ${linkTarget.name} ${linkTarget.dosage}`);
      setLinkTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save link");
    } finally {
      setSavingLink(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove the lab result for ${p.name} ${p.dosage}?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}/lab-result`);
      toast.success("Lab result removed");
      load();
    } catch {
      toast.error("Failed to remove lab result");
    }
  };

  const q = search.trim().toLowerCase();
  const visible = q
    ? products.filter((p) => `${p.name} ${p.dosage} ${p.category}`.toLowerCase().includes(q))
    : products;
  const withLab = products.filter((p) => p.lab_result_url).length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-[#8C7B6B] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="admin-lab-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search product, dosage or category…"
            className="pl-9"
          />
        </div>
        <p className="text-sm text-[#6E675F] sm:ml-auto" data-testid="admin-lab-count">
          <strong className="text-[#1A1A1A]">{withLab}</strong> of {products.length} products have lab results. PDF or image, max 10MB.
        </p>
      </div>

      <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" data-testid="admin-lab-file-input" onChange={handleFile} />

      <div className="border border-[#DDD7CF] rounded-lg bg-white overflow-x-auto">
        <Table data-testid="admin-lab-results-table">
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Lab Result</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-[#6E675F] py-10" data-testid="admin-lab-no-match">
                  No products match.
                </TableCell>
              </TableRow>
            )}
            {visible.map((p) => (
              <TableRow key={p.id} data-testid={`admin-lab-row-${p.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <img src={p.image_url} alt={p.name} className="w-10 h-10 rounded object-cover" />
                    <span className="font-medium">{p.name} <span className="text-[#6E675F] font-normal">{p.dosage}</span></span>
                  </div>
                </TableCell>
                <TableCell>
                  {p.lab_result_url ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <a href={p.lab_result_url} target="_blank" rel="noopener noreferrer" data-testid={`admin-lab-view-${p.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2D6A4F] bg-emerald-50 border border-emerald-200 rounded-none px-3 py-1 hover:bg-emerald-100 transition-colors">
                        <FileText className="w-3.5 h-3.5" /> View Report
                      </a>
                      <span className="text-[10px] uppercase tracking-wide font-semibold text-[#6E675F]" data-testid={`admin-lab-source-${p.id}`}>
                        {p.lab_result_url.startsWith("http") ? "External link" : "Uploaded file"}
                      </span>
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#6E675F]" data-testid={`admin-lab-missing-${p.id}`}>
                      <FlaskConical className="w-3.5 h-3.5" /> Not uploaded
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" disabled={uploadingId === p.id} data-testid={`admin-lab-upload-${p.id}`} onClick={() => pick(p)}>
                      <Upload className="w-4 h-4 mr-1" />
                      {uploadingId === p.id ? "Uploading…" : p.lab_result_url ? "Replace" : "Upload"}
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`admin-lab-link-${p.id}`} onClick={() => openLink(p)} title="Link to a third-party certificate URL">
                      <Link2 className="w-4 h-4 mr-1" /> Link
                    </Button>
                    {p.lab_result_url && (
                      <Button size="sm" variant="outline" data-testid={`admin-lab-remove-${p.id}`} onClick={() => remove(p)} className="text-red-700 border-red-200 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!linkTarget} onOpenChange={(open) => !open && setLinkTarget(null)}>
        <DialogContent data-testid="admin-lab-link-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">Link Lab Result</DialogTitle>
            <DialogDescription>
              Paste the third-party certificate URL for {linkTarget?.name} {linkTarget?.dosage} (e.g. a Janoshik
              verification link). Customers will open it directly from the Lab Results page.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="admin-lab-link-input"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://janoshik.com/tests/..."
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkTarget(null)}>Cancel</Button>
            <Button data-testid="admin-lab-link-save" onClick={saveLink} disabled={savingLink || !linkUrl.trim()} className="bg-[#8C7B6B] text-white hover:bg-[#75665A]">
              {savingLink ? "Saving…" : "Save Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
